import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Alert,
  IconButton,
  Divider,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { ReporterAvailability, JobType, TimeSlot, StatePreference } from '../types/availability';
import { availabilityApi } from '../utils/availabilityApi';
import { preferencesApi } from '../utils/preferencesApi';
import { HourSlotsEncoder } from '../utils/hourSlotsEncoder';

interface DailyAvailabilityEditorProps {
  open: boolean;
  selectedDate: Date;
  availability: ReporterAvailability | null;
  onClose: () => void;
  onSave: () => void;
}

// Generate time slots in 30-minute increments from 8 AM to 5 PM
const generateTimeSlots = (): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  for (let hour = 8; hour <= 17; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      // Don't add 17:30 (5:30 PM)
      if (hour === 17 && minute === 30) continue;
      
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push({
        start: timeString,
        end: `${hour === 17 ? '17:30' : (minute === 30 ? `${(hour + 1).toString().padStart(2, '0')}:00` : `${hour.toString().padStart(2, '0')}:30`)}`,
        isSelected: false,
        jobTypes: []
      });
    }
  }
  return slots;
};

const DailyAvailabilityEditor: React.FC<DailyAvailabilityEditorProps> = ({
  open,
  selectedDate,
  availability,
  onClose,
  onSave
}) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(generateTimeSlots());
  const [selectedJobTypes, setSelectedJobTypes] = useState<JobType[]>(['virtual']);
  const [notes, setNotes] = useState('');
  const [loading, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDefaultSchedule, setIsDefaultSchedule] = useState(false);
  const [hasExistingException, setHasExistingException] = useState(false);
  const [statePreferences, setStatePreferences] = useState<StatePreference[]>([]);
  const [isMarkedUnavailable, setIsMarkedUnavailable] = useState(false);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const displayDate = format(selectedDate, 'MMMM d, yyyy');

  // Load state preferences when editor opens
  useEffect(() => {
    if (open) {
      loadStatePreferences();
    }
  }, [open]);

  const loadStatePreferences = async () => {
    try {
      const prefs = await preferencesApi.getPreferences();
      if (prefs?.statePreferences) {
        setStatePreferences(prefs.statePreferences);
      }
    } catch (err) {
      console.error('Error loading state preferences:', err);
    }
  };

  // Initialize editor state when date changes
  useEffect(() => {
    if (!availability || !open) return;

    setError(null);
    
    // Check if there's an availability record for this date (handle both date formats)
    const availabilityRecord = availability.availability.find(record => {
      const recordDate = record.availabilityDate.includes('T') ? record.availabilityDate.split('T')[0] : record.availabilityDate;
      return recordDate === dateStr;
    });
    setHasExistingException(!!availabilityRecord);

    if (availabilityRecord) {
      // Load availability record data
      setNotes(availabilityRecord.notes || '');
      setIsMarkedUnavailable(availabilityRecord.unavailable || false);
      
      // If marked as unavailable, don't load hour slots
      if (availabilityRecord.unavailable) {
        setTimeSlots(generateTimeSlots());
        setSelectedJobTypes(getDefaultJobTypes());
      } else {
        // Decode hour slots to determine availability
        const hourSlotsData = HourSlotsEncoder.decode(availabilityRecord.hourSlots);
        const hasAvailability = HourSlotsEncoder.hasAvailability(availabilityRecord.hourSlots);
        
        if (!hasAvailability) {
          // No hours selected
          setTimeSlots(generateTimeSlots());
          setSelectedJobTypes(getDefaultJobTypes());
        } else {
        // Map hour slots data to time slots
        const slots = generateTimeSlots().map(slot => {
          const slotHour = parseInt(slot.start.split(':')[0]);
          const jobTypes = hourSlotsData[slotHour] || [];
          return {
            ...slot,
            isSelected: jobTypes.length > 0,
            jobTypes: jobTypes
          };
        });
          setTimeSlots(slots);
          
          // Set job types from hour slots encoding
          const allJobTypes = HourSlotsEncoder.getJobTypes(availabilityRecord.hourSlots);
          setSelectedJobTypes(allJobTypes.length > 0 ? allJobTypes : getDefaultJobTypes());
        }
      }
      setIsDefaultSchedule(false);
    } else {
      // No availability record - empty slots
      setTimeSlots(generateTimeSlots());
      setSelectedJobTypes(getDefaultJobTypes());
      setIsDefaultSchedule(false);
      setNotes('');
      setIsMarkedUnavailable(false);
    }
  // ESLINT NOTE: getDefaultJobTypes is derived purely from statePreferences which is already in the dep array,
  // so adding it would just cause the effect to re-run on every render (new function reference each time).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, availability, open, dateStr, statePreferences]);

  // Get default job types based on state preferences
  const getDefaultJobTypes = (): JobType[] => {
    const defaultTypes: JobType[] = [];
    
    // Check if any state has virtual availability
    if (statePreferences.some(pref => pref.virtualAvailable)) {
      defaultTypes.push('virtual');
    }
    
    // Check if any state has in-person availability
    if (statePreferences.some(pref => pref.inPersonAvailable)) {
      defaultTypes.push('in-person');
    }
    
    // If no preferences set, default to virtual only
    return defaultTypes.length > 0 ? defaultTypes : ['virtual'];
  };

  const handleTimeSlotClick = (index: number) => {
    setTimeSlots(prev => prev.map((slot, i) => 
      i === index 
        ? { 
            ...slot, 
            isSelected: !slot.isSelected,
            jobTypes: !slot.isSelected ? selectedJobTypes : []
          }
        : slot
    ));
  };

  const handleJobTypesChange = (event: React.MouseEvent<HTMLElement>, newJobTypes: JobType[]) => {
    if (newJobTypes.length > 0) {
      setSelectedJobTypes(newJobTypes);
      // Update selected slots with new job types
      setTimeSlots(prev => prev.map(slot => 
        slot.isSelected ? { ...slot, jobTypes: newJobTypes } : slot
      ));
    }
  };

  const handleQuickFill = (type: 'morning' | 'afternoon' | 'all' | 'clear') => {
    const newSlots = timeSlots.map(slot => {
      const hour = parseInt(slot.start.split(':')[0]);
      let shouldSelect = false;

      switch (type) {
        case 'morning':
          shouldSelect = hour >= 8 && hour < 12;
          break;
        case 'afternoon':
          shouldSelect = hour >= 13 && hour <= 17;
          break;
        case 'all':
          shouldSelect = true;
          break;
        case 'clear':
          shouldSelect = false;
          break;
      }

      return {
        ...slot,
        isSelected: shouldSelect,
        jobTypes: shouldSelect ? selectedJobTypes : []
      };
    });

    setTimeSlots(newSlots);
  };

  const handleUnavailableToggle = (checked: boolean) => {
    setIsMarkedUnavailable(checked);
    if (checked) {
      // Clear all selections when marking as unavailable
      setTimeSlots(generateTimeSlots());
      setNotes('');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // If marked as unavailable, save with unavailable flag
      if (isMarkedUnavailable) {
        await availabilityApi.setAvailability({
          availabilityDate: dateStr,
          hourSlots: 'NNNNNNNNNNNN',
          notes: '',
          unavailable: true
        });
        onSave();
        return;
      }

      const selectedSlots = timeSlots.filter(slot => slot.isSelected);
      
      if (selectedSlots.length === 0) {
        setError('Please select at least one time slot or mark the day as unavailable');
        return;
      }

      // Build hour slots object from selected time slots
      const hourSlotsData: Record<number, JobType[]> = {};
      selectedSlots.forEach(slot => {
        const hour = parseInt(slot.start.split(':')[0]);
        hourSlotsData[hour] = slot.jobTypes;
      });
      
      // Encode to hour slots string
      const hourSlotsString = HourSlotsEncoder.encode(hourSlotsData);

      await availabilityApi.setAvailability({
        availabilityDate: dateStr,
        hourSlots: hourSlotsString,
        notes,
        unavailable: false
      });

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveException = async () => {
    try {
      setSaving(true);
      setError(null);

      await availabilityApi.removeAvailability(dateStr);
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove exception');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            padding: 2
          }
        }}
      >
        <Box>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Edit Availability
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {displayDate}
          </Typography>

          {isDefaultSchedule && !hasExistingException && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing default schedule. Changes will create an exception for this date only.
            </Alert>
          )}

          {hasExistingException && (
            <Box mb={2}>
              <Chip
                label="Exception Day"
                color="secondary"
                size="small"
                onDelete={handleRemoveException}
              />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                This day has custom availability settings.
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Not Available Toggle */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isMarkedUnavailable}
                  onChange={(e) => handleUnavailableToggle(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography variant="subtitle2">
                  Mark as Not Available
                </Typography>
              }
            />
            {isMarkedUnavailable && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                When marked as not available, all hour selections will be cleared and you will appear as unavailable for the entire day.
              </Alert>
            )}
          </Box>

          {/* Job Type Selector */}
          <Typography variant="subtitle2" gutterBottom sx={{ opacity: isMarkedUnavailable ? 0.5 : 1 }}>
            Job Types:
          </Typography>
          <ToggleButtonGroup
            value={selectedJobTypes}
            onChange={handleJobTypesChange}
            size="small"
            sx={{ mb: 3, opacity: isMarkedUnavailable ? 0.5 : 1 }}
            disabled={isMarkedUnavailable}
          >
            <ToggleButton value="virtual">Virtual</ToggleButton>
            <ToggleButton value="in-person">In-Person</ToggleButton>
          </ToggleButtonGroup>

          {/* Quick Fill Buttons */}
          <Typography variant="subtitle2" gutterBottom sx={{ opacity: isMarkedUnavailable ? 0.5 : 1 }}>
            Quick Fill:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 2, opacity: isMarkedUnavailable ? 0.5 : 1 }}>
            <Button size="small" variant="outlined" onClick={() => handleQuickFill('morning')} disabled={isMarkedUnavailable}>
              Morning (8-12)
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickFill('afternoon')} disabled={isMarkedUnavailable}>
              Afternoon (1-5)
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickFill('all')} disabled={isMarkedUnavailable}>
              All Day (8-5)
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickFill('clear')} disabled={isMarkedUnavailable}>
              Clear All
            </Button>
          </Stack>

          {/* Time Slots Grid */}
          <Typography variant="subtitle2" gutterBottom sx={{ opacity: isMarkedUnavailable ? 0.5 : 1 }}>
            Available Time Slots:
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
              gap: 1,
              mb: 2,
              maxHeight: 200,
              overflowY: 'auto',
              opacity: isMarkedUnavailable ? 0.5 : 1
            }}
          >
            {timeSlots.map((slot, index) => (
              <Button
                key={index}
                variant={slot.isSelected ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleTimeSlotClick(index)}
                disabled={isMarkedUnavailable}
                sx={{
                  minHeight: 40,
                  fontSize: '0.75rem'
                }}
              >
                {slot.start}
              </Button>
            ))}
          </Box>

          {/* Notes */}
          <TextField
            label="Notes (optional)"
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Doctor's appointment in the morning"
            sx={{ mb: 2, opacity: isMarkedUnavailable ? 0.5 : 1 }}
            disabled={isMarkedUnavailable}
          />

          <Divider sx={{ my: 2 }} />

          {/* Action Buttons */}
          <Stack spacing={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Saving...' : 'Save Availability'}
            </Button>

            <Button
              variant="text"
              onClick={onClose}
              disabled={loading}
              fullWidth
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default DailyAvailabilityEditor;